// 2P AI — iscrizione Web Push (R5). Su iOS funziona SOLO da PWA aggiunta alla Home (display standalone)
// e il permesso si puo' chiedere solo da un gesto utente (click). Il DB resta la verita': un push e' un avviso.
import { api } from './api';

export type PushStatus = 'unsupported' | 'need-home' | 'default' | 'granted' | 'denied' | 'subscribed';

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// serviceWorker.ready non risolve mai se il SW non si attiva: race con un timeout per non restare appesi.
async function swReady(): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<ServiceWorkerRegistration>((_, rej) =>
      setTimeout(() => rej(new Error('Service worker non pronto (riapri l\'app dalla Home).')), 8000),
    ),
  ]);
}

export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return isStandalone() ? 'unsupported' : 'need-home';
  const perm = Notification.permission;
  if (perm === 'denied') return 'denied';
  if (perm === 'default') return 'default';
  try {
    const reg = await swReady();
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'subscribed' : 'granted';
  } catch {
    return 'granted';
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// Confronta la chiave applicationServerKey della sub esistente con quella attuale (rilevamento rotazione VAPID).
function sameServerKey(sub: PushSubscription, key: Uint8Array): boolean {
  const cur = sub.options.applicationServerKey;
  if (!cur) return false;
  const a = new Uint8Array(cur);
  if (a.length !== key.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== key[i]) return false;
  return true;
}

/** DA CHIAMARE da un gesto utente (click). Chiede il permesso, si iscrive, salva la subscription sul server. */
export async function enablePush(): Promise<PushStatus> {
  if (!pushSupported()) {
    throw new Error(isStandalone() ? 'Notifiche non supportate su questo browser.' : "Aggiungi prima l'app alla schermata Home.");
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'default';

  const { public_key } = await api.get<{ public_key: string }>('/notifications/vapid-key');
  if (!public_key) throw new Error('Chiave VAPID non ancora configurata sul server.');
  const appKey = urlBase64ToUint8Array(public_key);

  const reg = await swReady();
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameServerKey(sub, appKey)) {
    // chiave VAPID ruotata sul server → la vecchia sub e' morta: rifai.
    await sub.unsubscribe().catch(() => undefined);
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey as BufferSource,
    });
  }
  const json = sub.toJSON();
  await api.post('/notifications/subscribe', {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
    label: navigator.userAgent.slice(0, 80),
  });
  return 'subscribed';
}

export async function disablePush(): Promise<void> {
  try {
    const reg = await swReady();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post('/notifications/unsubscribe', { endpoint: sub.endpoint });
      await sub.unsubscribe();
    }
  } catch {
    /* best-effort */
  }
}
